/* eslint-disable no-restricted-imports */
import { useState } from 'react';
import useIngameCurrencyRules from '~/client/components/Game/hooks/useIngameCurrencyRules';
import type GameCurrencySelect from '../../GameCurrencySelect/useGameCurrencySelect/GameCurrency';
import GameFrame from '../GameFrame';
import usePlayGame from '../hooks/usePlayGame';
import Translation from '@/client/components/Translation';
import classNames from '@/external/classNames';
import Button from '@/external/components/Button';
import Dropdown from '@/external/components/Dropdown';
import MenuItem from '@/external/components/MenuItem';
import ControlsChevronDown from '@/external/icons/ControlsChevronDown';
import { CashierCurrencyType } from '@/graphql/schema';

type Props = {
  cashierCurrencyType?: CashierCurrencyType;
  cashierCurrency?: CashierCurrency;
  isLoggedIn: boolean;
  gameCurrencySelect: GameCurrencySelect;
  translations: Record<string, string>;
};

const PlayButtons: FC<Props> = ({
  cashierCurrency,
  cashierCurrencyType,
  isLoggedIn,
  gameCurrencySelect,
  translations,
}) => {
  const {
    canPlayFunMode,
    handlePlayForReal,
    handlePlayForFun,
    loadingSession,
    isGameStarted,
    goBackToDetails,
  } = usePlayGame();
  const [isFunMode, setFunMode] = useState(!isLoggedIn && canPlayFunMode);
  const { isGameLoadingDisabled } = useIngameCurrencyRules();
  const handleClickPlay = () => {
    if (isFunMode) {
      handlePlayForFun();
    } else {
      handlePlayForReal();
    }
  };

  return (
    <div>
      <div className="relative">
        <Button
          fullWidth
          onClick={handleClickPlay}
          animation={loadingSession ? 'progress' : undefined}
          disabled={loadingSession || isGameLoadingDisabled}
        >
          <Translation
            translations={translations}
            id={isFunMode ? 'game.fun_mode' : 'game.play_for_real'}
          />
        </Button>

        {canPlayFunMode && (
          <Dropdown
            value={isFunMode}
            onChange={setFunMode}
            className="absolute top-0 right-0 w-auto"
          >
            {({ open }) => (
              <>
                <Dropdown.Trigger>
                  <div className="rounded-moon-i-sm w-8 h-8 bg-goten/15 m-1 flex">
                    <ControlsChevronDown
                      className={classNames(
                        'text-goten m-auto transition-transform stroke-2',
                        open ? 'rotate-180' : 'rotate-0',
                      )}
                    />
                  </div>
                </Dropdown.Trigger>
                <Dropdown.Options menuWidth="w-56">
                  <Dropdown.Option value={false} key={'realMode'}>
                    {({ selected, active }) => (
                      <MenuItem
                        isActive={active}
                        isSelected={selected}
                        data-testid={`real-mode-option`}
                      >
                        <MenuItem.Title>
                          <Translation
                            id="game.play_for_real"
                            translations={translations}
                          />
                        </MenuItem.Title>
                        <MenuItem.Radio />
                      </MenuItem>
                    )}
                  </Dropdown.Option>
                  <Dropdown.Option value key={'funMode'}>
                    {({ selected, active }) => (
                      <MenuItem
                        isActive={active}
                        isSelected={selected}
                        data-testid={`fun-mode-option`}
                      >
                        <MenuItem.Title>
                          <Translation
                            id="game.fun_mode"
                            translations={translations}
                          />
                        </MenuItem.Title>
                        <MenuItem.Radio />
                      </MenuItem>
                    )}
                  </Dropdown.Option>
                </Dropdown.Options>
              </>
            )}
          </Dropdown>
        )}
      </div>
      {isGameStarted && (
        <GameFrame
          onClickBack={goBackToDetails}
          gameCurrencySelect={gameCurrencySelect}
          cashierCurrencyType={cashierCurrencyType}
          cashierCurrency={cashierCurrency}
          isLoggedIn={isLoggedIn}
        />
      )}
    </div>
  );
};

export default PlayButtons;
